/**
 * useLogoutAdapter — « Se déconnecter », porté sous les ports.
 *
 * Port littéral de `useLogout` (`common/account/LogoutButton.js`), que les ports ne peuvent pas
 * importer : on relit d'abord les comptes de la famille (`getFamilyTokens`) pour que « Qui utilise
 * Masteur ? » les montre à jour, et c'est À L'ISSUE de cette lecture — réussie ou non — que la vraie
 * déconnexion part : remise à zéro d'Amplitude, sur mobile les notifications effacées et l'appareil
 * désinscrit des notifications du compte quitté, l'état d'authentification vidé, puis `logout`.
 *
 * Deux écarts assumés avec le legacy, tous deux dans la règle des ports : aucune boîte d'erreur
 * (`useApiStatus` en montrait une sur un échec de `getFamilyTokens`, qui n'empêchait pourtant rien),
 * et `loggingOut` rendu à l'appelant, qui fait tourner son bouton jusqu'à l'issue — le legacy de
 * l'élève ne le savait pas.
 *
 * `getFamilyTokens` est aussi lu par la coquille legacy à chaque rechargement : seule la lecture que
 * NOUS avons lancée déclenche la déconnexion (`useApiMutation`, garde « est-ce nous »).
 *
 * Sous impersonation, se déconnecter, c'est sortir de la session impersonnée, comme le legacy et le
 * bandeau : `exitImpersonation` révoque le jeton et rend l'admin à ses comptes.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import * as Device from "expo-device"
import * as Notifications from "expo-notifications"
import { useCallback, useRef, useState } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { authConstants } from "../../../../data/constants/authConstants"
import { apiRequest } from "../../../../data/redux/actions/api"
import { authService } from "../../../../data/services/authService"
import { useApiMutation } from "./useApiMutation"
import { selectAccount } from "../../view-models/shared/displayedStudent"

export type LogoutAdapter = {
    /** Lance la déconnexion. Sans effet si elle est déjà en cours. */
    logout: () => void
    /** La déconnexion est en cours : le bouton tourne, les profils ne se choisissent plus. */
    loggingOut: boolean
}

export function useLogoutAdapter(): LogoutAdapter {
    const dispatch = useDispatch()
    const [loggingOut, setLoggingOut] = useState(false)
    const startedRef = useRef(false)
    const expoToken = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (state?.data?.session?.object?.expo_token as string | null | undefined) ?? null
    )

    // L'issue de `logout` : la session vidée, l'app repart sur « Qui utilise Masteur ? ». En échec,
    // on rend la main, sans quoi le bouton tournerait jusqu'au rechargement.
    const { start: startLogoutCall } = useApiMutation({
        service: authService.logout,
        // Pas un geste mesuré : il part après `amplitude.reset`, sous un visiteur anonyme.
        gesture: false,
        idleOnUnmount: [],
        onError: () => {
            startedRef.current = false
            setLoggingOut(false)
        },
    })

    const performActualLogout = useCallback(() => {
        amplitude.reset()
        if (Platform.OS !== "web") {
            void Notifications.dismissAllNotificationsAsync().then(() => {
                if (expoToken) {
                    dispatch(
                        apiRequest(authService.unsubscribeFromNotifications, {}, { expo_token: expoToken }) as never
                    )
                }
            })
            dispatch({ type: authConstants.RESET_AUTH_STATE })
        }
        startLogoutCall()
    }, [dispatch, expoToken, startLogoutCall])

    const { start: startFamilyTokens } = useApiMutation({
        service: authService.getFamilyTokens,
        // Une relecture avant la déconnexion, pas un geste de la personne.
        gesture: false,
        // La tranche est partagée avec la coquille legacy : on ne la rend pas au repos en partant.
        idleOnUnmount: [],
        onSuccess: performActualLogout,
        onError: performActualLogout,
    })

    const impersonating = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => !!selectAccount(state)?.impersonation
    )
    const { start: startExitImpersonation, requesting: exitingImpersonation } = useApiMutation({
        service: authService.exitImpersonation,
        skipWhilePending: true,
        // Le bandeau lit la même tranche : on ne la rend pas au repos en partant.
        idleOnUnmount: [],
    })

    const logout = useCallback(() => {
        if (impersonating) {
            startExitImpersonation()
            return
        }
        if (startedRef.current) return
        startedRef.current = true
        setLoggingOut(true)
        startFamilyTokens({}, { device_name: Platform.OS === "web" ? "WEB" : (Device.deviceName ?? "mobile") })
    }, [impersonating, startExitImpersonation, startFamilyTokens])

    return { logout, loggingOut: loggingOut || exitingImpersonation }
}
